import * as echarts from 'echarts'
import React from 'react'
import { ChartArea, classToFComponent, LifesHubColors } from 'src/utils/views'
import { makeStyles } from 'tss-react/mui'
import { JSX } from 'react/jsx-runtime'

import { EChartViewClass } from '../../../the-consumer-market/views/helpers/base-echart'
import AccessCard, { AccessCardHeader, AccessEmptyState } from './aa_card'
import { AccessChartScope } from '../aa_scopes'
import { formatDayLabel, formatNumber } from 'src/utils/formatter-utils'
import { formatNumberToCompactShortForm } from '../../../the-consumer-market/views/helpers/tcm_view_utils'

const AREA_TOP = LifesHubColors.chartBase
const AREA_BOTTOM = 'rgba(205, 235, 217, 0.08)'

const INK_SECONDARY = LifesHubColors.muted
const AXIS_LINE = LifesHubColors.border
const GRID_LINE = LifesHubColors.gridLine

const SERIES_COLOR = LifesHubColors.chartEmphasis

const useStyles = makeStyles()({
    view: {
        display: 'flex',
        alignSelf: 'stretch',
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 0,
        minHeight: 200
    }
})

type ChartDailyProps = {
    className?: string
    style?: React.CSSProperties
    scope: AccessChartScope
}

class ChartDailyViewClass extends EChartViewClass<ChartDailyProps> {
    // :: Fields

    scope!: AccessChartScope
    classes!: ReturnType<typeof useStyles>['classes']

    onSyncState(props: ChartDailyProps) {
        this.scope = props.scope
        this.classes = useStyles().classes
        this.chartData = buildChartData(this.scope)
        this.loaded = this.scope.loaded
        super.onSyncState(props)
    }

    override render({ className, style, scope }: ChartDailyProps): React.JSX.Element {
        return (
            <AccessCard elevation={0} className={className} style={style}>
                <AccessCardHeader title={scope.caption || 'Evolução diária'} />
                {scope.loaded && scope.rows.length === 0 ? (
                    <AccessEmptyState
                        title="Sem acessos no período"
                        text="Nenhum dia do intervalo selecionado registrou acesso."
                    />
                ) : (
                    <ChartArea>{super.render({ className: this.classes.view, scope })}</ChartArea>
                )}
            </AccessCard>
        )
    }
}

export const AccessChartDailyView = classToFComponent(ChartDailyViewClass, React)
export default AccessChartDailyView

function buildChartData(scope: AccessChartScope): echarts.EChartsOption {
    const labels = scope.rows.map((row) => formatDayLabel(row.period))
    const values = scope.rows.map((row) => row.accesses)

    const showSymbol = values.length <= 40

    return {
        grid: { left: 8, right: 16, top: 28, bottom: 4, containLabel: true },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'line' },
            valueFormatter: (value) => formatNumber(Number(value))
        },
        xAxis: {
            type: 'category',
            data: labels,
            boundaryGap: false,
            axisTick: { show: false },
            axisLine: { lineStyle: { color: AXIS_LINE } },
            axisLabel: { color: INK_SECONDARY }
        },
        yAxis: {
            type: 'value',
            splitLine: { lineStyle: { color: GRID_LINE } },
            axisLabel: { color: INK_SECONDARY, formatter: (value: number) => formatNumberToCompactShortForm(value) }
        },
        series: [
            {
                name: 'Acessos',
                type: 'line',
                data: values,
                lineStyle: { width: 2, color: SERIES_COLOR },
                itemStyle: { color: SERIES_COLOR, borderColor: '#ffffff', borderWidth: 2 },
                symbol: 'circle',
                symbolSize: 8,
                showSymbol,
                areaStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: AREA_TOP },
                        { offset: 1, color: AREA_BOTTOM }
                    ])
                }
            }
        ]
    }
}
